import { useEffect, useState } from "react";
import { fetchAsset } from "@/lib/api/asset";

interface AuthenticatedAssetState {
  /** URL locale (blob:) utilisable dans <img>, <iframe>, <a href> */
  url: string | null;
  loading: boolean;
  error: boolean;
}

/**
 * Télécharge un asset via apiClient (avec JWT) et expose un blob URL local.
 * Libère automatiquement l'URL quand l'assetId change ou au démontage.
 *
 * @param assetId  ID de l'asset à charger (null = pas de chargement)
 * @param retryKey Incrémenter pour forcer un re-fetch du même asset
 */
export function useAuthenticatedAssetUrl(
  assetId: string | null,
  retryKey = 0,
): AuthenticatedAssetState {
  const [state, setState] = useState<AuthenticatedAssetState>({
    url: null,
    loading: false,
    error: false,
  });

  useEffect(() => {
    if (!assetId) {
      setState({ url: null, loading: false, error: false });
      return;
    }

    let revoked = false;
    let objectUrl: string | null = null;

    setState({ url: null, loading: true, error: false });

    fetchAsset(assetId)
      .then((blob) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ url: objectUrl, loading: false, error: false });
      })
      .catch(() => {
        if (revoked) return;
        setState({ url: null, loading: false, error: true });
      });

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [assetId, retryKey]);

  return state;
}
